import React, { useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Link, Navigate, useParams } from "react-router-dom"
import ContainerPage from "../../../components/ContainerPage/ContainerPage"
import PageTitle from "../../../components/PageTitle/PageTitle"
import Button from "../../../components/Button/Button"
import { selectFeatureFlag, selectFeatureFlagsSettled } from "../../../features/featureFlags/selectors"
import { ACCOUNTING_DAILY_CSV_EMAIL } from "../../../config/featureFlagKeys"
import { downloadDailyFile } from "../../../features/accountingDailyCsv/accountingDailyCsv"
import { safeGetItem, USER } from "../../../lib/storage"
import { ACTIVE_COMPANY_KEY } from "../../../lib/ambientHeaders"

const idOf = (value) => (value && typeof value === 'object' ? value._id : value) || null

const ownFirmId = () => {
  try {
    return idOf(JSON.parse(safeGetItem(USER) || 'null')?.company)
  } catch (error) {
    return null
  }
}

const worksInAnotherBrand = () => {
  const brand = safeGetItem(ACTIVE_COMPANY_KEY)
  const own = ownFirmId()
  return Boolean(brand && own && String(brand) !== String(own))
}

const triggerDownload = (file, fileName) => {
  const link = document.createElement('a')
  link.href = file
  link.download = fileName
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
}

const DailyFile = () => {
  const dispatch = useDispatch()
  const { fileId } = useParams()
  const flagOn = useSelector(selectFeatureFlag(ACCOUNTING_DAILY_CSV_EMAIL))
  const settled = useSelector(selectFeatureFlagsSettled)
  const [result, setResult] = useState({ status: 'loading' })
  const requested = useRef(false)
  const mayAsk = flagOn || worksInAnotherBrand()

  useEffect(() => {
    if (!settled || !mayAsk || requested.current) return
    requested.current = true
    dispatch(downloadDailyFile(fileId)).then((action) => {
      if (action.error) {
        setResult({ status: 'error', httpStatus: action.payload?.status, routed: action.payload?.routed })
        return
      }
      const data = action.payload
      triggerDownload(data.file, data.fileName)
      setResult({ status: 'done', data })
    })
  }, [settled, mayAsk, fileId, dispatch])

  if (!settled) return null
  if (!mayAsk) return <Navigate to="/" replace />
  if (!flagOn && result.status === 'loading') return null
  if (!flagOn && result.httpStatus === 404 && !result.routed) return <Navigate to="/" replace />

  const renderBody = () => {
    if (result.status === 'loading') return <p>Preparing your file...</p>
    if (result.status === 'done') {
      const { data } = result
      return (
        <div className="flex flex-col items-start gap-4">
          <p>
            Your Accounting file for {data.day}{data.officeName ? ` · ${data.officeName}` : ''} ({data.rowCount} payments) has downloaded.
          </p>
          <Button type="outlined" onClick={() => triggerDownload(data.file, data.fileName)} classes="border !border-primary-main h-10">
            Download again
          </Button>
        </div>
      )
    }
    if (result.httpStatus === 410) {
      return (
        <p>
          This file has expired. Export it again from the <Link to="/accounting" className="text-primary-main underline">Accounting page</Link>.
        </p>
      )
    }
    if (result.httpStatus === 403 || result.httpStatus === 404) {
      return <p>This file is not available for your login.</p>
    }
    return <p>Something went wrong while loading the file. Please try again later.</p>
  }

  return (
    <ContainerPage>
      <PageTitle title="Accounting file" />
      <div className="p-4 bg-surface border border-divider shadow-custom rounded-2xl">
        {renderBody()}
      </div>
    </ContainerPage>
  )
}

export default DailyFile
